import type { Employee, PulseQuestion, PulseSurvey } from "@shared/schema";
import { Send } from "lucide-react";
import { useLocation } from "wouter";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { formatDate, formatDateTime } from "@/lib/constants";

type InstanceWithStats = {
  id: string;
  pulseSurveyId: string;
  sentAt: Date | string | null;
  deadline: Date | string | null;
  status: string;
  directReportsTotal?: number;
  directReportsResponded?: number;
};

type ResponseWithDetails = {
  id: string;
  employeeId: string;
  status: string;
  submittedAt: Date | string | null;
  employee?: Employee;
};

type PendingResponse = {
  id: string;
  employeeId: string;
  employee?: Employee;
};

type SurveyDetail = PulseSurvey & {
  questions?: PulseQuestion[];
  responses?: ResponseWithDetails[];
  instances?: InstanceWithStats[];
  pendingResponses?: PendingResponse[];
  totalExpected?: number;
  finishedCount?: number;
  pendingCount?: number;
};

interface ManagerSurveyDetailViewProps {
  survey: SurveyDetail;
}

export function ManagerSurveyDetailView({ survey }: ManagerSurveyDetailViewProps) {
  const [, setLocation] = useLocation();

  const instances = survey.instances || [];
  const surveyStatus = survey.isActive ? "active" : "closed";

  const columns = [
    {
      key: "sentAt",
      header: "Enviada em",
      cell: (instance: InstanceWithStats) => (
        <div>
          <p className="text-sm font-medium">{formatDateTime(instance.sentAt)}</p>
          <p className="text-xs text-muted-foreground">Prazo: {formatDate(instance.deadline)}</p>
        </div>
      ),
    },
    {
      key: "responses",
      header: "Respostas",
      cell: (instance: InstanceWithStats) => {
        const responded = instance.directReportsResponded || 0;
        const total = instance.directReportsTotal || 0;
        const rate = total > 0 ? Math.round((responded / total) * 100) : 0;
        return (
          <div className="flex items-center gap-3 min-w-[160px]">
            <Progress value={rate} className="h-2 flex-1" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {responded}/{total} ({rate}%)
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (instance: InstanceWithStats) => <StatusBadge status={instance.status} />,
    },
  ];

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title={survey.title}
        description="Envios e respostas dos seus liderados"
        onBack={() => setLocation("/surveys")}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="h-4 w-4" />
                Envios da Pesquisa
              </CardTitle>
              <CardDescription>
                {instances.length === 0
                  ? "Nenhum envio realizado ainda"
                  : `${instances.length} envio${instances.length > 1 ? "s" : ""} realizado${instances.length > 1 ? "s" : ""}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={columns}
                data={instances}
                emptyMessage="Nenhum envio realizado ainda"
                testIdPrefix="instance"
                onRowClick={(instance) => setLocation(`/surveys/instances/${instance.id}`)}
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                <StatusBadge status={surveyStatus} />
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Total de Envios</p>
                <p className="text-sm">{instances.length}</p>
              </div>
              {survey.description && (
                <>
                  <Separator />
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Descrição</p>
                    <p className="text-sm">{survey.description}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
